import React, {
  useRef,
  useState,
  useEffect,
  PropsWithChildren,
  useCallback,
} from "react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { classes } from "lib/actions/util";
import TooltipArrow from "components/elements/Tooltips/TooltipArrow";
import { sideNavTooltipBaseClassName } from "components/layouts/elements/SideNav/SideNavTooltip";

/** The side nav tooltip look, wrapping long names instead of staying on one line */
const rowTooltipClassName = `${sideNavTooltipBaseClassName} max-w-sm break-all`;

/**
 * OverflowTooltip is a component that displays a tooltip when the content overflows its container.
 * It uses Floating UI for tooltip positioning/interactions and checks for overflow on window resize.
 *
 * @param props - `className` is an HTML class that gets passed onto the rendered wrapped component;
 * `customTooltipContent` is the optional content to display inside the tooltip instead of the children;
 * `appearance` is "row" for the side nav tooltip look with a slower open, used on list rows so it matches their icon tooltips.
 * @returns {React.JSX.Element} The wrapped content plus a tooltip portal when needed.
 */
const OverflowTooltip = ({
  children,
  className,
  customTooltipContent = null,
  appearance = "default",
}: PropsWithChildren<{
  className?: string | null;
  customTooltipContent?: React.ReactNode | null;
  appearance?: "default" | "row";
}>): React.JSX.Element => {
  const contentsWrapperRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowPresent, setIsOverflowPresent] = useState<boolean>(false);

  const {
    refs,
    floatingStyles,
    context,
    placement: finalPlacement,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "top",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  const hover = useHover(context, {
    delay: { open: appearance === "row" ? 1500 : 200, close: 100 },
    move: false,
    enabled: isOverflowPresent,
  });
  const focus = useFocus(context, { enabled: isOverflowPresent });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const determineIfOverflowIsPresent = useCallback(() => {
    const node = contentsWrapperRef.current;
    if (!node) return;

    node.style.overflow = "visible";
    const contentWidth = node.getBoundingClientRect().width;
    node.style.overflow = "hidden";
    const clientWidth = node.getBoundingClientRect().width;

    setIsOverflowPresent(contentWidth > clientWidth);
  }, []);

  useEffect(() => {
    determineIfOverflowIsPresent();
    window.addEventListener("resize", determineIfOverflowIsPresent);

    return () =>
      window.removeEventListener("resize", determineIfOverflowIsPresent);
  }, [children, determineIfOverflowIsPresent]);

  useEffect(() => {
    if (!isOverflowPresent) {
      setIsOpen(false);
    }
  }, [isOverflowPresent]);

  return (
    <>
      <div
        ref={(node) => {
          contentsWrapperRef.current = node;
          refs.setReference(node);
        }}
        {...getReferenceProps()}
        className={classes("text-ellipsis whitespace-nowrap", className)}
      >
        {children}
      </div>
      {isOpen && isOverflowPresent && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={
              appearance === "row"
                ? rowTooltipClassName
                : "pointer-events-none z-50 max-w-sm overflow-hidden break-all rounded-md bg-white px-2 py-1 text-sm text-gray-900 shadow-md ring-1 ring-black/10"
            }
          >
            {appearance === "row" && (
              <TooltipArrow placement={finalPlacement} />
            )}
            {customTooltipContent ?? children}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default OverflowTooltip;
